var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useState, forwardRef, useImperativeHandle } from "react";
import classNames from "classnames";
import useDebounce from "@hooks/useDebounce";
import useHasChanged from "@hooks/useHasChanged";
import SearchBox from "@widget/forms/inputs/SearchBox";
import SortableTreeNode from './SortableTreeNode';
import { hasValue } from "@utils";
import { UXPContext } from "@core/context/Context";
import { TreeViewLoader } from "@widget/loaders";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { getNodeDepth, canDropIntoNode, customCollisionDetection } from './treeview-drag-utils';
import { extractTreeViewStyles, findTreeNodeByIdSync, processAllTreeNodes, calculateDropPosition, generateIdWithPrefix } from "./treeview-utils";
import Icon from "@icon/Icon";
/**
 *
 * @export
 *
 * TreeView component renders a hierarchical structure of nodes with optional icons, labels, and selectable functionality.
 *
 * @example
 *
 * ```
 * <TreeView
 *    items={[
 *      { id: '1', label: 'Root Node', children: [{ id: '1-1', label: 'Child Node' }] }
 *    ]}
 *    selected={selectedNode}
 *    onSelect={handleSelectNode}
 *    showPath={true}
 *    styles={{
 *      nodeBGColor: '#f0f0f0',
 *      labelTextColor: '#333',
 *    }}
 * />
 * ```
 *
 */
const TreeView = forwardRef((props, ref) => {
    var _a;
    const context = useContext(UXPContext);
    const [loading, setLoading] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [internalDragState, setInternalDragState] = useState({
        isDragging: false,
        draggedNodeId: null,
        dropzoneNodeId: null,
        isDragSource: true,
        draggedNode: null,
        dropPosition: null
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const [mousePosition, setMousePosition] = useState(null);
    const query = useDebounce(searchText, 300);
    const hasItemsChanged = useHasChanged(props.items);
    // Set up drag sensors with activation threshold
    // 8px movement prevents accidental drags when clicking nodes
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 8,
        },
    }));
    const enableMultiSelect = (props === null || props === void 0 ? void 0 : props.multiSelect) || false;
    const selectedItems = enableMultiSelect ? (!!(props === null || props === void 0 ? void 0 : props.selected) && (props === null || props === void 0 ? void 0 : props.selected) instanceof Array && ((_a = props === null || props === void 0 ? void 0 : props.selected) === null || _a === void 0 ? void 0 : _a.length) > 0) ? props.selected : [] : (props === null || props === void 0 ? void 0 : props.selected) || null;
    // Check if drag & drop should be enabled
    const isDragDropEnabled = (props.enableDrag || props.enableReordering || props.enableHierarchyChange) &&
        !(enableMultiSelect && (selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) > 1);
    // Use external loading if provided, otherwise use internal loading state
    const isLoading = props.loading ? props.loading : loading;
    const isOperationLoading = isLoading || isProcessing || props.reorderLoading;
    // Calculate all path-related values in JavaScript for better browser compatibility
    const treeViewStyles = extractTreeViewStyles(props, context);
    // Drag state management for both internal and cross-tree drag operations
    // When externalDragState is provided (cross-tree scenario):
    //   - isDragging: true indicates ANY drag is happening globally
    //   - isDragSource: true means THIS tree is being dragged FROM
    // This allows target trees to show drop indicators even though they're not the drag source
    const dragState = (props === null || props === void 0 ? void 0 : props.externalDragState) || internalDragState;
    useEffect(() => {
        if (hasItemsChanged)
            processInitialItems();
    }, [props.items, hasItemsChanged]);
    useEffect(() => {
        var _a;
        if ((props === null || props === void 0 ? void 0 : props.enableSearch) && !!(props === null || props === void 0 ? void 0 : props.onSearch) && typeof (props === null || props === void 0 ? void 0 : props.onSearch) == 'function') {
            (_a = props === null || props === void 0 ? void 0 : props.onSearch) === null || _a === void 0 ? void 0 : _a.call(props, query);
        }
    }, [query]);
    // Track mouse position during drag for accurate drop position calculation
    useEffect(() => {
        // Only track during drag
        if (!dragState.isDragging) {
            setMousePosition(null);
            return;
        }
        const handlePointerMove = (e) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };
        document.addEventListener('pointermove', handlePointerMove);
        return () => {
            document.removeEventListener('pointermove', handlePointerMove);
        };
    }, [dragState.isDragging]);
    function processInitialItems() {
        // On first load (no nodes yet) show the loading skeleton.
        // On subsequent updates pass the existing nodes so expand/collapse state
        // and scroll position are preserved (no content flash).
        const isFirstLoad = nodes.length === 0;
        if (isFirstLoad)
            setLoading(true);
        processAllTreeNodes(nodes, props.items, props === null || props === void 0 ? void 0 : props.expandAllNodes)
            .then(res => {
            setNodes(res);
            setLoading(false);
        })
            .catch(() => {
            setNodes([]);
            setLoading(false);
        });
    }
    function handleSelect(node, isDisabled, hasSeleceted) {
        var _a, _b, _c;
        if (!isDisabled) {
            if (enableMultiSelect) {
                if (hasSeleceted) {
                    (_a = props === null || props === void 0 ? void 0 : props.onSelect) === null || _a === void 0 ? void 0 : _a.call(props, (selectedItems || []).filter(n => n.id !== node.id));
                }
                else {
                    (_b = props === null || props === void 0 ? void 0 : props.onSelect) === null || _b === void 0 ? void 0 : _b.call(props, [...selectedItems, node]);
                }
            }
            else {
                (_c = props === null || props === void 0 ? void 0 : props.onSelect) === null || _c === void 0 ? void 0 : _c.call(props, node);
            }
        }
    }
    function handleToggleNode(node) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Set the node's expanding state to true
                const updatedNodes = updateNodeState(nodes, node.id, { expanding: true });
                setNodes(updatedNodes);
                // If the node is already expanded, collapse it
                if (node.expanded) {
                    const collapsedNodes = updateNodeState(nodes, node.id, { expanded: false, expanding: false });
                    setNodes(collapsedNodes);
                    return;
                }
                // If the node has no children and getChildren is a function, load the children
                if (node.getChildren && typeof node.getChildren === 'function' && node.children.length === 0) {
                    try {
                        const result = yield node.getChildren(node);
                        const updatedNodes = updateNodeState(nodes, node.id, {
                            children: yield processAllTreeNodes(nodes, result.nodes, props === null || props === void 0 ? void 0 : props.expandAllNodes),
                            expanded: true,
                            expanding: false,
                        });
                        setNodes(updatedNodes);
                    }
                    catch (error) {
                        console.error(`Unable to load children for node ${node.id}. Error: `, error);
                        const updatedNodes = updateNodeState(nodes, node.id, { expanding: false });
                        setNodes(updatedNodes);
                    }
                }
                else {
                    // If children are already available, simply toggle expansion
                    const updatedNodes = updateNodeState(nodes, node.id, { expanded: true, expanding: false });
                    setNodes(updatedNodes);
                }
            }
            catch (error) {
                console.error(`Unable to toggle node ${node.id}. Error: `, error);
                const updatedNodes = updateNodeState(nodes, node.id, { expanding: false });
                setNodes(updatedNodes);
            }
        });
    }
    function updateNodeState(nodes, nodeId, updatedProperties) {
        return nodes.map((node) => {
            if (node.id === nodeId) {
                // Update the node properties
                return Object.assign(Object.assign({}, node), updatedProperties);
            }
            else if (node.children && node.children.length > 0) {
                // Recursively update children
                return Object.assign(Object.assign({}, node), { children: updateNodeState(node.children, nodeId, updatedProperties) });
            }
            else {
                return node;
            }
        });
    }
    const resetInternalDragState = () => {
        setInternalDragState({
            isDragging: false,
            draggedNodeId: null,
            dropzoneNodeId: null,
            isDragSource: true,
            draggedNode: null,
            dropPosition: null
        });
    };
    function setAllExpanded(nodes, expanded) {
        return nodes.map(n => (Object.assign(Object.assign({}, n), { expanded, children: n.children ? setAllExpanded(n.children, expanded) : n.children })));
    }
    useImperativeHandle(ref, () => ({
        triggerDrop: (sourceNodeId, targetNodeId, dropPosition) => __awaiter(void 0, void 0, void 0, function* () {
            yield processDrop(sourceNodeId, targetNodeId, dropPosition);
        }),
        expandAll: () => setNodes(prev => setAllExpanded(prev, true)),
        collapseAll: () => setNodes(prev => setAllExpanded(prev, false)),
    }), []);
    const handleDragStart = (event) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const nodeId = ((_a = event === null || event === void 0 ? void 0 : event.active) === null || _a === void 0 ? void 0 : _a.id) || null;
        if (!nodeId)
            return;
        const node = findTreeNodeByIdSync(nodes, nodeId);
        setInternalDragState({
            isDragging: true,
            draggedNodeId: nodeId,
            draggedNode: node,
            dropPosition: null
        });
    });
    const handleDragOver = (event) => {
        var _a, _b;
        const targetId = (_a = event === null || event === void 0 ? void 0 : event.over) === null || _a === void 0 ? void 0 : _a.id;
        if (!targetId)
            return;
        const targetNode = findTreeNodeByIdSync(nodes, targetId);
        if (!targetNode)
            return;
        // Get target rectangle from @dnd-kit
        const overRect = (_b = event.over) === null || _b === void 0 ? void 0 : _b.rect;
        let dropPosition = null;
        if (overRect && mousePosition) {
            // Always use 30/30/40 split so "child" is reachable even for siblings
            dropPosition = calculateDropPosition(mousePosition.y, overRect, false);
        }
        setInternalDragState(prev => (Object.assign(Object.assign({}, prev), { dropzoneNodeId: targetId, dropPosition: dropPosition })));
    };
    const handleDragMove = (event) => {
        var _a, _b;
        const targetId = (_a = event === null || event === void 0 ? void 0 : event.over) === null || _a === void 0 ? void 0 : _a.id;
        // If not over any node, clear drop position
        if (!targetId) {
            setInternalDragState(prev => (Object.assign(Object.assign({}, prev), { dropPosition: null })));
            return;
        }
        const targetNode = findTreeNodeByIdSync(nodes, targetId);
        if (!targetNode)
            return;
        // Get target rectangle from @dnd-kit
        const overRect = (_b = event.over) === null || _b === void 0 ? void 0 : _b.rect;
        let dropPosition = null;
        if (overRect && mousePosition) {
            // Always use 30/30/40 split so "child" is reachable even for siblings
            dropPosition = calculateDropPosition(mousePosition.y, overRect, false);
        }
        // Only update drop position (don't change dropzoneNodeId)
        setInternalDragState(prev => (Object.assign(Object.assign({}, prev), { dropPosition: dropPosition })));
    };
    /**
     * Process a drop operation - used by both handleDragEnd and triggerDrop
     */
    const processDrop = (sourceNodeId, targetNodeId, dropPosition) => __awaiter(void 0, void 0, void 0, function* () {
        // Validate source and target are not the same
        if (sourceNodeId === targetNodeId) {
            return;
        }
        // Get actual nodes
        const sourceNode = findTreeNodeByIdSync(nodes, sourceNodeId);
        const targetNode = findTreeNodeByIdSync(nodes, targetNodeId);
        // Validate nodes and dropPosition exist
        if (!targetNode || !sourceNode || !dropPosition) {
            return;
        }
        // Handle different drop types based on drop position
        try {
            switch (dropPosition) {
                case 'before':
                    yield handleInsertBefore(sourceNode, targetNode);
                    break;
                case 'after':
                    yield handleInsertAfter(sourceNode, targetNode);
                    break;
                case 'child':
                    yield handleAddAsChild(sourceNode, targetNode);
                    break;
            }
        }
        catch (error) {
            console.error('Error during drop processing:', error);
            throw error;
        }
    });
    const handleDragEnd = (event) => __awaiter(void 0, void 0, void 0, function* () {
        const { active: source, over: target } = event;
        const { dropPosition } = internalDragState;
        try {
            // no target or no dropPosition - cancel drag
            if (!target || !dropPosition) {
                return;
            }
            yield processDrop(source.id, target.id, dropPosition);
        }
        catch (error) {
            console.error('Error during drag end:', error);
        }
        finally {
            resetInternalDragState();
        }
    });
    // Helper function to get node index within its siblings
    function getNodeIndex(node) {
        const parentId = node.parentId;
        let siblings;
        if (parentId) {
            const parent = findTreeNodeByIdSync(nodes, parentId);
            siblings = (parent === null || parent === void 0 ? void 0 : parent.children) || [];
        }
        else {
            siblings = nodes;
        }
        return siblings.findIndex(n => n.id === node.id);
    }
    const handleInsertBefore = (sourceNode, targetNode) => __awaiter(void 0, void 0, void 0, function* () {
        // Insert sourceNode before targetNode (as sibling)
        if (props.enableReordering && sourceNode.parentId === targetNode.parentId) {
            // Same parent - reorder
            yield handleReorder(sourceNode, targetNode, 'before');
        }
        else if (props.enableHierarchyChange && props.onHierarchyChange) {
            // Different parent - hierarchy change, insert at target's index
            yield props.onHierarchyChange(sourceNode, targetNode.parentId || null, getNodeIndex(targetNode));
        }
    });
    const handleInsertAfter = (sourceNode, targetNode) => __awaiter(void 0, void 0, void 0, function* () {
        // Insert sourceNode after targetNode (as sibling)
        if (props.enableReordering && sourceNode.parentId === targetNode.parentId) {
            // Same parent - reorder
            yield handleReorder(sourceNode, targetNode, 'after');
        }
        else if (props.enableHierarchyChange && props.onHierarchyChange) {
            // Different parent - hierarchy change, insert after target
            yield props.onHierarchyChange(sourceNode, targetNode.parentId || null, getNodeIndex(targetNode) + 1);
        }
    });
    const handleAddAsChild = (sourceNode, targetNode) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        // Add sourceNode as last child of targetNode
        if (props.enableHierarchyChange && props.onHierarchyChange) {
            if (canDropIntoNode(sourceNode, targetNode, nodes, props.maxDepth)) {
                yield props.onHierarchyChange(sourceNode, targetNode.id, ((_a = targetNode.children) === null || _a === void 0 ? void 0 : _a.length) || 0);
            }
            else {
                console.warn('Invalid drop: Cannot move node to this location (max depth or circular reference)');
            }
        }
    });
    const handleReorder = (sourceNode, targetNode, position) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setIsProcessing(true);
            // Find the parent and its children
            const parentId = sourceNode.parentId;
            let siblings;
            if (parentId) {
                const parent = findTreeNodeByIdSync(nodes, parentId);
                if (!parent)
                    return;
                siblings = parent.children;
            }
            else {
                // Root level
                siblings = nodes;
            }
            // Calculate indices for reordering
            const oldIndex = siblings.findIndex(n => n.id === sourceNode.id);
            const targetIndex = siblings.findIndex(n => n.id === targetNode.id);
            // Adjust target index based on position
            // For 'after': when oldIndex < targetIndex, target shifts left after removal, so use targetIndex
            //              when oldIndex > targetIndex, target position unchanged, so use targetIndex + 1
            const adjustedIndex = position === 'after'
                ? (oldIndex < targetIndex ? targetIndex : targetIndex + 1)
                : targetIndex;
            // Reorder siblings using arrayMove
            const reordered = arrayMove(siblings, oldIndex, adjustedIndex);
            const newIndex = reordered.findIndex(n => n.id === sourceNode.id);
            // Call the callback
            if (props.onReorder) {
                const done = yield props.onReorder(reordered, sourceNode, newIndex, parentId);
                if (done) {
                    if (parentId) {
                        const updatedNodes = updateNodeState(nodes, parentId, { children: reordered });
                        setNodes(updatedNodes);
                    }
                    else {
                        setNodes(reordered);
                    }
                }
            }
        }
        catch (error) {
            console.error('Error during reorder:', error);
        }
        finally {
            setIsProcessing(false);
        }
    });
    function renderAddRoot() {
        if (!(props === null || props === void 0 ? void 0 : props.enableAdd))
            return null;
        return _jsxs("div", { className: classNames("uxpcore_treeview__add-root-node"), onClick: () => {
                var _a;
                (_a = props === null || props === void 0 ? void 0 : props.onAdd) === null || _a === void 0 ? void 0 : _a.call(props);
            }, children: ["+ ", (props === null || props === void 0 ? void 0 : props.addButtonLabel) || 'add'] });
    }
    function renderActionButtons(node) {
        var _a, _b;
        // Check depth restriction for adding children
        let canAddChildren = ((node === null || node === void 0 ? void 0 : node.enableAddChildren) !== false) && (node.enableAddChildren || props.enableAdd);
        if (canAddChildren && props.maxDepth !== undefined) {
            const nodeDepth = getNodeDepth(node.id, nodes);
            if (nodeDepth >= props.maxDepth) {
                canAddChildren = false; // At max depth, can't add children
            }
        }
        const canEdit = ((node === null || node === void 0 ? void 0 : node.enableEditNode) !== false) && (node.enableEditNode || props.enableEdit);
        const canDelete = ((node === null || node === void 0 ? void 0 : node.enableDeleteNode) !== false) && (node.enableDeleteNode || props.enableDelete);
        const hasCustomActions = !!(props === null || props === void 0 ? void 0 : props.customActions);
        if (!canEdit && !canDelete && !canAddChildren && !hasCustomActions)
            return null;
        return _jsxs("div", { className: classNames("uxpcore_treeview__node-actions", { 'uxpcore_treeview__node-actions--show': !props.showActionButtonOnlyOnHover }), children: [canAddChildren ? _jsx("div", { className: "uxpcore_treeview__node-action-btn", onClick: (e) => {
                        var _a;
                        e.preventDefault();
                        e.stopPropagation();
                        (_a = props === null || props === void 0 ? void 0 : props.onAdd) === null || _a === void 0 ? void 0 : _a.call(props, node);
                    }, children: _jsx(Icon, { icon: ['fal', 'plus'] }) }) : null, canEdit ? _jsx("div", { className: "uxpcore_treeview__node-action-btn", onClick: (e) => {
                        var _a;
                        e.preventDefault();
                        e.stopPropagation();
                        (_a = props === null || props === void 0 ? void 0 : props.onEdit) === null || _a === void 0 ? void 0 : _a.call(props, node);
                    }, children: _jsx(Icon, { icon: ['fal', 'pencil'] }) }) : null, canDelete ? _jsx("div", { className: "uxpcore_treeview__node-action-btn", onClick: (e) => {
                        var _a;
                        e.preventDefault();
                        e.stopPropagation();
                        (_a = props === null || props === void 0 ? void 0 : props.onDelete) === null || _a === void 0 ? void 0 : _a.call(props, node.id);
                    }, children: _jsx(Icon, { icon: ['fal', 'trash'] }) }) : null, hasCustomActions && ((_b = (_a = props.customActions) === null || _a === void 0 ? void 0 : _a.call(props, node)) === null || _b === void 0 ? void 0 : _b.map((action, index) => (_jsx("div", { className: classNames("uxpcore_treeview__node-action-btn"), onClick: (e) => {
                        var _a;
                        e.preventDefault();
                        e.stopPropagation();
                        (_a = action === null || action === void 0 ? void 0 : action.onClick) === null || _a === void 0 ? void 0 : _a.call(action, node);
                    }, title: action.tooltip, children: _jsx(Icon, { icon: action.icon }) }, index))))] });
    }
    function renderIcon(icon) {
        if (!hasValue(icon))
            return null;
        return _jsx("div", { className: "uxpcore_treeview__node-icon-container", children: _jsx(Icon, { icon: icon }) });
    }
    function renderNodes(_nodes, childNodes, isParentDisabled) {
        var _a;
        // Wrap nodes in SortableContext for drag & drop
        const nodeIds = _nodes.map(n => generateIdWithPrefix(n.id, props.idPrefixForComparison));
        const effectiveDraggedNode = ((_a = props.externalDragState) === null || _a === void 0 ? void 0 : _a.draggedNodeId)
            ? findTreeNodeByIdSync(nodes, props.externalDragState.draggedNodeId) || {
                id: props.externalDragState.draggedNodeId,
                label: 'External Node',
                nodeId: props.externalDragState.draggedNodeId,
                children: [],
                path: '',
            }
            : internalDragState === null || internalDragState === void 0 ? void 0 : internalDragState.draggedNode;
        const renderedNodes = (_nodes || []).map((node, i) => (_jsx(SortableTreeNode, { node: node, childNodes: childNodes, isParentDisabled: isParentDisabled, isLastNode: i === (_nodes || []).length - 1, isFirstNode: i === 0, isDragDropEnabled: isDragDropEnabled, isOperationLoading: isOperationLoading, overId: dragState.dropzoneNodeId ? generateIdWithPrefix(dragState.dropzoneNodeId, props.idPrefixForComparison) : null, isDragging: dragState.isDragging, draggedNode: effectiveDraggedNode, allNodes: nodes, maxDepth: props.maxDepth, enableHierarchyChange: props.enableHierarchyChange, enableMultiSelect: enableMultiSelect, selectedItems: selectedItems, actionButtonsPosition: props.actionButtonsPosition, renderIcon: renderIcon, handleToggleNode: handleToggleNode, handleSelect: handleSelect, renderActionButtons: renderActionButtons, renderNodes: renderNodes, renderLabel: props.renderLabel, renderDetails: props.renderDetails, styles: props.styles, isCrossTreeDrag: !!props.externalDragState && !props.externalDragState.isDragSource, dropPosition: dragState.dropPosition, idPrefixForComparison: props === null || props === void 0 ? void 0 : props.idPrefixForComparison }, node.id)));
        // Wrap in SortableContext if drag & drop is enabled
        if (isDragDropEnabled) {
            return (_jsx(SortableContext, { items: nodeIds, strategy: verticalListSortingStrategy, children: renderedNodes }));
        }
        return renderedNodes;
    }
    const treeContent = _jsxs("div", { className: classNames("uxpcore_treeview__container", {
            'uxpcore_treeview__container--show-path': (props === null || props === void 0 ? void 0 : props.showPath) != false,
            'uxpcore_treeview__container--horizontal-roots': props === null || props === void 0 ? void 0 : props.renderRootNodesHorizontally,
        }), style: treeViewStyles, children: [(props === null || props === void 0 ? void 0 : props.showHeader)
                ? _jsxs("div", { className: "uxpcore_treeview__header", children: [_jsx("div", { className: "uxpcore_treeview__title", children: !!(props === null || props === void 0 ? void 0 : props.title) ? props.title : null }), _jsx("div", { className: "uxpcore_treeview__search", children: (props === null || props === void 0 ? void 0 : props.enableSearch)
                                ? _jsx(SearchBox, { value: searchText, onChange: setSearchText })
                                : null })] })
                : null, (isLoading) ? _jsx(_Fragment, { children: _jsx(TreeViewLoader, {}) })
                : _jsx(_Fragment, { children: ((nodes === null || nodes === void 0 ? void 0 : nodes.length) == 0)
                        ? _jsxs("div", { className: "uxpcore_treeview__noitems", children: [hasValue(props.noItemsMessage) ? props.noItemsMessage : 'No items found', renderAddRoot()] })
                        : _jsx("div", { className: "uxpcore_treeview__content", children: _jsxs("div", { className: "uxpcore_treeview__tree", children: [renderNodes(nodes || []), renderAddRoot()] }) }) })] });
    // Wrap in DndContext if drag & drop is enabled
    // For cross-tree drag operations, disableInternalDndContext is true and the parent
    // component (e.g., TreeViewCompare) provides a single DndContext for both trees
    // This ensures collision detection works across tree boundaries
    if (isDragDropEnabled && !(props === null || props === void 0 ? void 0 : props.disableInternalDndContext)) {
        return (_jsxs(DndContext, { sensors: sensors, collisionDetection: customCollisionDetection, onDragStart: handleDragStart, onDragOver: handleDragOver, onDragMove: handleDragMove, onDragEnd: handleDragEnd, children: [treeContent, _jsx(DragOverlay, { children: (internalDragState === null || internalDragState === void 0 ? void 0 : internalDragState.draggedNode) ? (_jsx("div", { className: "uxpcore_treeview__drag-overlay", children: _jsx("div", { className: "uxpcore_treeview__drag-overlay-content", children: _jsx("div", { className: "uxpcore_treeview__drag-overlay-label", children: internalDragState === null || internalDragState === void 0 ? void 0 : internalDragState.draggedNode.label }) }) })) : null }), isProcessing || props.reorderLoading
                    ? _jsx("div", { className: "uxpcore_treeview__reordering-loading", children: _jsx(TreeViewLoader, {}) })
                    : null] }));
    }
    return treeContent;
});
export default TreeView;
